// src/lib/localCache.js
//
// Local cache layer for Death Card offline-first data flow.
// Wraps IndexedDB via the `idb` library. The ONLY module that talks
// directly to IndexedDB. All higher-level code (db.js, outbox.js)
// goes through this interface.
//
// Adapter pattern: when we port to React Native, we swap this file
// for an AsyncStorage/SQLite implementation. Nothing above it changes.

import { getDb, STORES } from './idbSchema'

// --- public API ---

export async function get(table, id) {
  const db = await getDb()
  return db.get(table, id) ?? null
}

export async function getAll(table, filterFn) {
  const db = await getDb()
  const all = await db.getAll(table)
  return filterFn ? all.filter(filterFn) : all
}

export async function put(table, row) {
  if (!row || !row.id) {
    throw new Error(`[localCache] put(${table}) requires a row with an id`)
  }
  const db = await getDb()
  await db.put(table, row)
}

export async function putMany(table, rows) {
  if (!Array.isArray(rows)) {
    throw new Error(`[localCache] putMany(${table}) requires an array`)
  }
  if (rows.length === 0) return
  const db = await getDb()
  const tx = db.transaction(table, 'readwrite')
  await Promise.all([
    ...rows.map(row => tx.store.put(row)),
    tx.done,
  ])
}

export async function del(table, id) {
  const db = await getDb()
  await db.delete(table, id)
}

export async function clear(table) {
  const db = await getDb()
  await db.clear(table)
}

export async function clearAll() {
  // Used on signOut — nuke every cached table.
  // STORES is an object ({ CACHE: [...names], OUTBOX: 'outbox' }), not an
  // array — iterate STORES.CACHE which is the list of cache table names
  // and is what the transaction was already opened against.
  const db = await getDb()
  const tx = db.transaction(STORES.CACHE, 'readwrite')
  await Promise.all([
    ...STORES.CACHE.map(name => tx.objectStore(name).clear()),
    tx.done,
  ])
}

// Bundle as default export so callers can do `import cache from './localCache'`
// or named imports like `import { get } from './localCache'`. Both work.
export default {
  get,
  getAll,
  put,
  putMany,
  delete: del,  // can't name an export `delete` — reserved word
  clear,
  clearAll,
}